import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Check, X } from "lucide-react";
import { useAppDispatch } from "../../../redux/store";
import { setTopNavNameTitle } from "../../../redux/features/mobileLayout";
import Select from "react-select";

interface FilterState {
    departmentCodes: string[];
    groupCodes: string[];
    siteCodes: string[];
    fields: string[];
}

// Mock data for departments, groups, sites
const mockDepartments = [
    { value: "dept123", label: "Marketing" },
    { value: "dept456", label: "Engineering" },
    { value: "dept789", label: "Finance" }
];

const mockGroups = [
    { value: "testgroup", label: "Test Group" },
    { value: "group123", label: "Group 123" },
    { value: "group456", label: "Group 456" }
];

const mockSites = [
    { value: "1234", label: "Main Office" },
    { value: "5678", label: "North Campus" },
    { value: "9012", label: "South Campus" }
];

const fieldOptions = [
    { value: "total_hours", label: "Total Hours" },
    { value: "total_overtime", label: "Overtime Hours" },
    { value: "doubletime_hours", label: "Doubletime Hours" },
    { value: "total_break_minutes", label: "Break Minutes" }
];

const ManagerTimesheetFilters: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    
    // Get initial filters from location state or use defaults
    const initialFilters = (location.state as FilterState) || {
        departmentCodes: [],
        groupCodes: [],
        siteCodes: [],
        fields: ["total_hours"]
    };
    
    const [filters, setFilters] = useState<FilterState>(initialFilters);
    
    // Format department options for react-select
    const [selectedDepartments, setSelectedDepartments] = useState(
        mockDepartments.filter(dept => filters.departmentCodes.includes(dept.value))
    );
    
    const [selectedGroups, setSelectedGroups] = useState(
        mockGroups.filter(group => filters.groupCodes.includes(group.value))
    );
    
    const [selectedSites, setSelectedSites] = useState(
        mockSites.filter(site => filters.siteCodes.includes(site.value))
    );
    
    const [selectedFields, setSelectedFields] = useState(
        fieldOptions.filter(field => filters.fields.includes(field.value))
    );

    useEffect(() => {
        dispatch(setTopNavNameTitle("Timesheet Filters"));
    }, [dispatch]);

    const handleDepartmentChange = (selected: any) => {
        setSelectedDepartments(selected || []);
        setFilters(prev => ({
            ...prev,
            departmentCodes: (selected || []).map((item: any) => item.value)
        }));
    };
    
    const handleGroupChange = (selected: any) => {
        setSelectedGroups(selected || []);
        setFilters(prev => ({
            ...prev,
            groupCodes: (selected || []).map((item: any) => item.value)
        }));
    };
    
    const handleSiteChange = (selected: any) => {
        setSelectedSites(selected || []);
        setFilters(prev => ({
            ...prev,
            siteCodes: (selected || []).map((item: any) => item.value)
        }));
    };
    
    const handleFieldChange = (selected: any) => {
        setSelectedFields(selected || []);
        setFilters(prev => ({
            ...prev,
            fields: (selected || []).map((item: any) => item.value)
        }));
    };

    const handleApplyFilters = () => {
        // Store filters in localStorage for persistence
        localStorage.setItem("managerTimesheetFilters", JSON.stringify(filters));
        
        // Navigate back to employee view with updated filters
        navigate("/m-manager-timesheet", { state: filters });
    };
    
    const handleClearFilters = () => {
        const emptyFilters = {
            departmentCodes: [],
            groupCodes: [],
            siteCodes: [],
            fields: ["total_hours"]
        };
        
        setFilters(emptyFilters);
        setSelectedDepartments([]);
        setSelectedGroups([]);
        setSelectedSites([]);
        setSelectedFields(fieldOptions.filter(field => field.value === "total_hours"));
        
        localStorage.removeItem("managerTimesheetFilters");
    };
    
    const handleCancel = () => {
        navigate("/m-manager-timesheet");
    };

    return (
        <div className="container mx-auto bg-white h-full">
            <div className="flex flex-col text-gray-800 text-base font-sans h-full">
                <div className="flex items-center justify-between px-4 py-3 border-b">
                    <button 
                        onClick={handleCancel}
                        className="flex items-center text-blue-500"
                    >
                        <X size={18} />
                        <span className="ml-1">Cancel</span>
                    </button>
                    
                    <div className="font-medium">Filter Options</div>
                    
                    <button
                        onClick={handleApplyFilters}
                        className="flex items-center text-blue-500"
                    >
                        <Check size={18} />
                        <span className="ml-1">Apply</span>
                    </button>
                </div>
                
                <div className="flex-1 overflow-y-auto p-4">
                    <div className="mb-6">
                        <div className="flex justify-between items-center mb-2">
                            <label className="text-sm text-gray-700 font-medium">Departments</label>
                            <button 
                                className="text-xs text-blue-500"
                                onClick={() => setSelectedDepartments([])}
                            >
                                Clear
                            </button>
                        </div>
                        
                        <Select 
                            isMulti
                            value={selectedDepartments}
                            onChange={handleDepartmentChange}
                            options={mockDepartments}
                            className="text-sm"
                            classNamePrefix="react-select"
                            placeholder="Select Departments"
                        />
                    </div>
                    
                    <div className="mb-6">
                        <div className="flex justify-between items-center mb-2">
                            <label className="text-sm text-gray-700 font-medium">Groups</label>
                            <button 
                                className="text-xs text-blue-500"
                                onClick={() => setSelectedGroups([])}
                            >
                                Clear
                            </button>
                        </div>
                        
                        <Select
                            isMulti
                            value={selectedGroups}
                            onChange={handleGroupChange}
                            options={mockGroups}
                            className="text-sm"
                            classNamePrefix="react-select"
                            placeholder="Select Groups"
                        />
                    </div>
                    
                    <div className="mb-6">
                        <div className="flex justify-between items-center mb-2">
                            <label className="text-sm text-gray-700 font-medium">Sites</label>
                            <button 
                                className="text-xs text-blue-500"
                                onClick={() => setSelectedSites([])}
                            >
                                Clear
                            </button>
                        </div>
                        
                        <Select
                            isMulti
                            value={selectedSites}
                            onChange={handleSiteChange}
                            options={mockSites}
                            className="text-sm"
                            classNamePrefix="react-select"
                            placeholder="Select Sites"
                        />
                    </div>
                    
                    {/* <div className="mb-6">
                        <div className="flex justify-between items-center mb-2">
                            <label className="text-sm text-gray-700 font-medium">Data Fields</label>
                            <button 
                                className="text-xs text-blue-500"
                                onClick={() => setSelectedFields([fieldOptions[0]])}
                            >
                                Reset
                            </button>
                        </div>
                        
                        <Select
                            isMulti
                            value={selectedFields}
                            onChange={handleFieldChange}
                            options={fieldOptions}
                            className="text-sm"
                            classNamePrefix="react-select"
                            placeholder="Select Data Fields"
                        />
                    </div> */}
                    
                    <div className="my-6">
                        <button
                            onClick={handleClearFilters}
                            className="w-full py-3 text-center border border-gray-300 rounded-md text-gray-700 font-medium"
                        >
                            Clear All Filters
                        </button>
                    </div>
                    
                    {/* <div className="mb-6">
                        <button
                            onClick={() => {
                                // Save as preset - this would implement saving named presets
                                alert("This would save the current filter set as a preset");
                            }}
                            className="w-full py-3 text-center bg-blue-500 text-white rounded-md font-medium"
                        >
                            Save as Preset
                        </button>
                    </div> */}
                </div>
            </div>
        </div>
    );
};

export default ManagerTimesheetFilters;
